import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const Solutions = () => {
  const sections = useRef([]);
  const [activeCategory, setActiveCategory] = useState('Software Solutions');

  useEffect(() => {
    const observers = [];
    sections.current.forEach((section) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeInScale');
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.1 }
      );
      if (section) observer.observe(section);
      observers.push(observer);
    });

    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  useEffect(() => {
    let touchStartY = 0;
    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };
    const handleTouchMove = (e) => {
      const touchEndY = e.touches[0].clientY;
      const deltaY = touchStartY - touchEndY;
      if (deltaY > 30) {
        window.scrollBy({ top: 75, behavior: 'smooth' });
      }
    };
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const shapes = [
    { type: 'circle', size: 'w-16 h-16', color: 'bg-blue-400', x: '10vw', y: '10vh', delay: 0 },
    { type: 'triangle', size: 'w-12 h-12', color: 'bg-purple-600', x: '25vw', y: '30vh', delay: 0.3 },
    { type: 'square', size: 'w-18 h-18', color: 'bg-blue-500', x: '40vw', y: '15vh', delay: 0.6 },
    { type: 'circle', size: 'w-10 h-10', color: 'bg-purple-500', x: '60vw', y: '25vh', delay: 0.9 },
    { type: 'triangle', size: 'w-14 h-14', color: 'bg-blue-300', x: '15vw', y: '50vh', delay: 1.2 },
    { type: 'square', size: 'w-16 h-16', color: 'bg-purple-400', x: '35vw', y: '60vh', delay: 1.5 },
    { type: 'circle', size: 'w-20 h-20', color: 'bg-blue-600', x: '50vw', y: '40vh', delay: 1.8 },
    { type: 'triangle', size: 'w-12 h-12', color: 'bg-purple-600', x: '70vw', y: '20vh', delay: 2.1 },
    { type: 'circle', size: 'w-14 h-14', color: 'bg-blue-400', x: '20vw', y: '70vh', delay: 2.4 },
    { type: 'square', size: 'w-10 h-10', color: 'bg-purple-500', x: '45vw', y: '10vh', delay: 2.7 },
    { type: 'triangle', size: 'w-16 h-16', color: 'bg-blue-300', x: '65vw', y: '55vh', delay: 3.0 },
    { type: 'circle', size: 'w-18 h-18', color: 'bg-blue-500', x: '30vw', y: '35vh', delay: 3.3 },
    { type: 'square', size: 'w-12 h-12', color: 'bg-purple-400', x: '80vw', y: '45vh', delay: 3.6 },
    { type: 'triangle', size: 'w-14 h-14', color: 'bg-blue-600', x: '10vw', y: '25vh', delay: 3.9 },
    { type: 'circle', size: 'w-16 h-16', color: 'bg-purple-500', x: '55vw', y: '65vh', delay: 4.2 },
    { type: 'square', size: 'w-10 h-10', color: 'bg-blue-400', x: '75vw', y: '15vh', delay: 4.5 },
  ];

  const categories = [
    {
      title: 'Software Solutions',
      items: [
        { img: 'https://images.unsplash.com/photo-1516321310762-479437144403?auto=format&fit=crop&w=300&q=80', name: 'Web Applications', desc: 'Custom web apps for seamless user experiences.' },
        { img: 'https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6?auto=format&fit=crop&w=300&q=80', name: 'Mobile Apps', desc: 'Cross-platform mobile applications.' },
        { img: 'https://images.unsplash.com/photo-1535223289827-42f1e9919769?auto=format&fit=crop&w=300&q=80', name: 'Desktop Software', desc: 'Robust desktop applications.' },
      ],
    },
    {
      title: 'Cloud Services',
      items: [
        { img: 'https://images.unsplash.com/photo-1451187580459-43490279c0b2?auto=format&fit=crop&w=300&q=80', name: 'Cloud Migration', desc: 'Seamless transition to cloud platforms.' },
        { img: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=300&q=80', name: 'Cloud Infrastructure', desc: 'Scalable and secure cloud setups.' },
        { img: 'https://images.unsplash.com/photo-1516321310762-479437144403?auto=format&fit=crop&w=300&q=80', name: 'Cloud Management', desc: 'Optimized cloud resource management.' },
      ],
    },
    {
      title: 'Cybersecurity',
      items: [
        { img: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=300&q=80', name: 'Network Security', desc: 'Protecting your network infrastructure.' },
        { img: 'https://images.unsplash.com/photo-1563986768494-4dee2763f6e2?auto=format&fit=crop&w=300&q=80', name: 'Data Encryption', desc: 'Securing sensitive data with encryption.' },
        { img: 'https://images.unsplash.com/photo-1516321310762-479437144403?auto=format&fit=crop&w=300&q=80', name: 'Threat Detection', desc: 'Advanced threat monitoring and response.' },
      ],
    },
    {
      title: 'AI Solutions',
      items: [
        { img: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=300&q=80', name: 'AI Automation', desc: 'Automate processes with AI.' },
        { img: 'https://images.unsplash.com/photo-1516321310762-479437144403?auto=format&fit=crop&w=300&q=80', name: 'Machine Learning', desc: 'Custom ML models for your business.' },
        { img: 'https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6?auto=format&fit=crop&w=300&q=80', name: 'Chatbots', desc: 'Intelligent conversational agents.' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-950">
      <section
        ref={(el) => (sections.current[0] = el)}
        className="relative min-h-[850px] text-white flex items-center justify-center text-center overflow-hidden opacity-0 py-20"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950 to-blue-900/90 z-0"></div>
        {shapes.map((shape, index) => (
          <motion.div
            key={index}
            className={`${shape.type === 'circle' ? 'rounded-full' : shape.type === 'triangle' ? 'triangle' : 'square'} ${shape.size} ${shape.color} absolute opacity-30`}
            initial={{ x: shape.x, y: shape.y, scale: 1, rotate: 0 }}
            animate={{
              x: `calc(${shape.x} + ${Math.random() * 20 + 20}px)`,
              y: `calc(${shape.y} - ${Math.random() * 20 + 20}px)`,
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.6, 0.3],
              rotate: [0, Math.random() * 30 - 15, 0],
            }}
            transition={{
              repeat: Infinity,
              repeatType: 'reverse',
              duration: 4 + Math.random() * 2,
              delay: shape.delay,
              ease: 'easeInOut',
            }}
          />
        ))}
        <div className="relative z-10 px-6 sm:px-10 lg:px-24 max-w-5xl">
          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-8 text-shadow-lg text-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our IT Solutions
          </motion.h1>
          <motion.p
            className="text-lg sm:text-xl md:text-2xl text-gray-100 mb-10 leading-relaxed text-shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Explore our comprehensive IT solutions designed to empower your business with cutting-edge technology.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row justify-center gap-6 mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <a
              href="#contact"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-7 py-4 rounded-lg shadow-xl transition duration-300"
            >
              Request a Consultation
            </a>
            <a
              href="#services"
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-7 py-4 rounded-lg shadow-xl transition duration-300"
            >
              Explore Services
            </a>
          </motion.div>
          <motion.p
            className="text-base text-gray-200 italic mt-6 text-shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Empowering businesses globally for over <span className="font-semibold text-white">12 years</span>.
          </motion.p>
        </div>
      </section>

      <section
        ref={(el) => (sections.current[1] = el)}
        className="py-20 bg-gray-950 opacity-0"
      >
        <div className="container mx-auto px-6">
          <nav className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.title}
                onClick={() => setActiveCategory(category.title)}
                className={`px-6 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 border-2 ${
                  activeCategory === category.title
                    ? 'bg-blue-600 text-white border-blue-600 scale-105 shadow-md'
                    : 'border-gray-700 text-gray-300 hover:text-white hover:border-blue-400 hover:scale-105'
                }`}
              >
                {category.title}
              </button>
            ))}
          </nav>
        </div>
      </section>

      <section
        ref={(el) => (sections.current[2] = el)}
        className="py-20 bg-gray-950 opacity-0"
      >
        <div className="container mx-auto px-6">
          <h2 className="text-4xl sm:text-5xl font-bold text-center mb-14 text-white text-shadow-lg">
            {activeCategory}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10">
            {categories.find((cat) => cat.title === activeCategory).items.map((item, i) => {
              const message = encodeURIComponent(`Hello, I'm interested in the solution: ${item.name}`);
              return (
                <div
                  key={i}
                  className="bg-gray-850 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition duration-300 w-full max-w-xs mx-auto"
                >
                  <img
                    src={item.img}
                    alt={item.name}
                    className="h-40 mx-auto mb-6 rounded-lg"
                  />
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">
                    {item.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {item.desc}
                  </p>
                  <a
                    href={`https://wa.me/919123456789?text=${message}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition"
                  >
                    Inquiry
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="bg-gray-950 text-gray-300 py-6 text-center">
        <p>© 2025 CmCoreTech. All rights reserved.</p>
        <p className="mt-2">Contact: cmcoretech@gmail.com | +91 87806 43219</p>
      </footer>
    </div>
  );
};

export default Solutions;
